import React, { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  FaBullseye,
  FaRegEye,
  FaPhoneAlt,
  FaWhatsapp,
  FaCheckCircle,
  FaUserMd,
  FaShieldAlt,
  FaHandsHelping,
  FaClinicMedical,
  FaHeartbeat,
  FaNotesMedical,
  FaSearch,
  FaMapMarkerAlt,
} from "react-icons/fa";
import drAnushaImg from "../assets/dr.anusha.jpeg";
import drSpandanaImg from "../assets/dr.spandana.jpeg";

export default function About() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [visibleSections, setVisibleSections] = useState([]);
  const sectionRefs = useRef([]);

  const whatsappNumber = "9182643439";
  const whatsappLink = `https://wa.me/${whatsappNumber}`;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.dataset.index);
          if (entry.isIntersecting) {
            setVisibleSections((prev) =>
              prev.includes(index) ? prev : [...prev, index]
            );
          }
        });
      },
      { threshold: 0.14 }
    );

    sectionRefs.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const missionPoints = [
    "Deliver evidence-based rehabilitation",
    "Prevent unnecessary surgeries where possible",
    "Reduce chronic pain conditions",
    "Promote long-term musculoskeletal health",
    "Educate patients about prevention and posture",
  ];

  const clinicalApproach = [
    {
      title: "Comprehensive Assessment",
      text: "Every patient undergoes detailed musculoskeletal and functional evaluation.",
    },
    {
      title: "Root Cause Identification",
      text: "We treat the source of pain, not just the symptoms.",
    },
    {
      title: "Customized Rehabilitation Plan",
      text: "No generic exercise sheets. Every program is individualized.",
    },
    {
      title: "Progressive Recovery Protocol",
      text: "Treatment evolves based on measurable improvement.",
    },
    {
      title: "Prevention & Maintenance",
      text: "Long-term care guidance to prevent recurrence.",
    },
  ];

  const team = [
    {
      name: "Dr. Anusha",
      qualifications: "BPT (NIMS), MIAP, CKTP",
      role: "Clinical Lead",
      image: drAnushaImg,
      description: [
        "Dr. Anusha is a dedicated physiotherapist with specialized expertise in orthopedic and spine rehabilitation.",
        "She combines scientific assessment with compassionate patient care, ensuring every individual feels heard, understood, and guided throughout recovery. Her approach is hands-on, structured, and result-oriented."
      ],
      interestsTitle: "Areas of Clinical Interest",
      interests: [
        "Advanced Spine Rehabilitation",
        "Orthopedic Physiotherapy",
        "Post-Operative Rehabilitation",
        "Sports Injury Management",
        "Postural & Ergonomic Correction",
        "Manual Therapy & Kinesio Taping",
      ]
    },
    {
      name: "Dr. Spandana Bpt",
      qualifications: "BPT, Tirumala college of physiotherapy (Nizamabad)",
      role: "Physiotherapist",
      image: drSpandanaImg,
      description: [
        "Dr. Spandana is a skilled physiotherapist with 2 years of experience.",
        "She is dedicated to providing high-quality care and personalized rehabilitation programs to help patients achieve optimal recovery."
      ],
      interestsTitle: "Specialties",
      interests: [
        "Manual therapy",
        "Dry needling",
        "Cupping therapy",
        "Post op rehabilitation",
        "Back and neck pain treatment"
      ]
    }
  ];

  const values = [
    {
      title: "Integrity",
      text: "Honest treatment plans with no unnecessary procedures.",
      icon: <FaShieldAlt />,
    },
    {
      title: "Clinical Excellence",
      text: "Up-to-date, evidence-based physiotherapy techniques.",
      icon: <FaClinicMedical />,
    },
    {
      title: "Patient Education",
      text: "Helping patients understand their condition and recovery process.",
      icon: <FaHandsHelping />,
    },
    {
      title: "Commitment",
      text: "We take responsibility for your recovery journey.",
      icon: <FaHeartbeat />,
    },
  ];

  const serviceAreas = [
    "KPHB Colony",
    "Miyapur",
    "Moosapet",
    "Hitech City",
    "Nizampet",
    "Surrounding areas of Hyderabad",
  ];

  const whyChooseUs = [
    {
      title: "Evidence-Based & Structured Rehabilitation",
      text: "We follow scientifically validated physiotherapy protocols with assessment, functional evaluation, root cause analysis, and a customized rehabilitation roadmap.",
      icon: <FaNotesMedical />,
    },
    {
      title: "One-to-One Personalized Sessions",
      text: "Dedicated attention, individualized exercise prescription, real-time correction, and continuous progress monitoring.",
      icon: <FaUserMd />,
    },
    {
      title: "Root Cause Diagnosis",
      text: "We identify biomechanical imbalances, muscle weakness patterns, postural dysfunction, and movement compensation errors.",
      icon: <FaSearch />,
    },
    {
      title: "Ethical & Transparent Care",
      text: "Honest recovery timelines, clear explanations, no unnecessary sessions, and no exaggerated promises.",
      icon: <FaShieldAlt />,
    },
  ];

  const revealClass = (index) =>
    `reveal ${visibleSections.includes(index) ? "show" : ""}`;

  return (
    <>
      <Navbar />

      <style>{`
        html {
          scroll-behavior: smooth;
        }

        body {
          background: #f4f7fb;
        }

        .reveal {
          opacity: 0;
          transform: translateY(26px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }

        .reveal.show {
          opacity: 1;
          transform: translateY(0);
        }

        .page-card {
          background: #ffffff;
          border: 1px solid #e7edf5;
          border-radius: 24px;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
          transition: transform 0.28s ease, box-shadow 0.28s ease;
        }

        .page-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 36px rgba(15, 23, 42, 0.09);
        }

        .mini-card {
          background: linear-gradient(180deg, #ffffff 0%, #f8fbfd 100%);
          border: 1px solid #e7edf5;
          border-radius: 18px;
          padding: 18px 16px;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }

        .mini-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
        }

        .hero-btn {
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }

        .hero-btn:hover {
          transform: translateY(-3px);
        }

        .section-title {
          margin: 0 0 16px;
          color: #182338;
          font-weight: 800;
          line-height: 1.15;
          font-family: Georgia, serif;
        }

        .section-text {
          color: #617186;
          line-height: 1.85;
          font-size: 16px;
        }

        .check-item {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          color: #5f7086;
          line-height: 1.8;
          font-size: 15px;
        }

        .icon-box {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-bottom: 16px;
        }

        .value-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          background: linear-gradient(135deg, #2ea7ff, #36d36b);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
          font-size: 18px;
        }

        .number-badge {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: linear-gradient(135deg, #2ea7ff, #37d067);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          margin-bottom: 14px;
        }

        .contact-strip {
          display: flex;
          flex-wrap: wrap;
          gap: 18px;
          margin-top: 24px;
          color: #dce7f5;
          font-size: 15px;
        }

        .contact-chip {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border-radius: 999px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.1);
        }

        .whatsapp-float {
          position: fixed;
          right: 18px;
          bottom: 18px;
          width: 58px;
          height: 58px;
          border-radius: 50%;
          background: #25D366;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          font-size: 28px;
          z-index: 9999;
          box-shadow: 0 12px 24px rgba(37, 211, 102, 0.32);
          transition: transform 0.25s ease;
        }

        .whatsapp-float:hover {
          transform: translateY(-3px) scale(1.04);
        }

        @media (max-width: 900px) {
          .section-text {
            font-size: 15px;
          }

          .contact-strip {
            gap: 12px;
          }

          .contact-chip {
            width: 100%;
            justify-content: flex-start;
          }
        }
      `}</style>

      <div style={{ background: "#f4f7fb", overflowX: "hidden" }}>
        {/* HERO */}
        <section
          style={{
            background:
              "linear-gradient(90deg, #032a34 0%, #071c47 42%, #0a2961 100%)",
            padding: isMobile ? "54px 16px 96px" : "70px 28px 130px",
          }}
        >
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <div
              style={{
                display: "inline-block",
                padding: "10px 16px",
                borderRadius: "999px",
                background: "rgba(46,214,122,0.12)",
                color: "#b8f7d4",
                fontWeight: 800,
                fontSize: "12px",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: "20px",
              }}
            >
              About Us
            </div>

            <h1
              style={{
                margin: 0,
                color: "#ffffff",
                fontSize: isMobile ? "2.35rem" : "4.1rem",
                lineHeight: 1.08,
                fontWeight: 800,
                fontFamily: "Georgia, serif",
              }}
            >
              Elite Care Physiotherapy
              <br />
              <span style={{ color: "#2ed67a" }}>& Rehabilitation</span>
            </h1>

            <p
              style={{
                margin: "18px 0 0",
                color: "#d6dfed",
                fontSize: isMobile ? "16px" : "19px",
                lineHeight: 1.8,
                maxWidth: "900px",
              }}
            >
              Kukatpally, Hyderabad — advanced rehabilitation focused on
              restoring mobility, rebuilding strength, and improving quality of
              life through evidence-based care.
            </p>

            <div
              style={{
                marginTop: "26px",
                display: "flex",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="hero-btn"
                style={{
                  textDecoration: "none",
                  background: "#2ed67a",
                  color: "#fff",
                  fontWeight: 800,
                  padding: "15px 26px",
                  borderRadius: "999px",
                  boxShadow: "0 10px 24px rgba(46,214,122,0.24)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                Book Consultation <FaWhatsapp />
              </a>

              <a
                href="#why-choose-us"
                className="hero-btn"
                style={{
                  textDecoration: "none",
                  background: "transparent",
                  color: "#e6eef9",
                  fontWeight: 700,
                  padding: "14px 24px",
                  borderRadius: "999px",
                  border: "1px solid rgba(255,255,255,0.28)",
                }}
              >
                Why Choose Us
              </a>
            </div>


          </div>
        </section>

        {/* WHO WE ARE */}
        <section
          style={{
            padding: isMobile ? "0 16px 42px" : "0 24px 56px",
            marginTop: isMobile ? "-58px" : "-82px",
          }}
        >
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <div
              className={`${revealClass(0)} page-card`}
              data-index={0}
              ref={(el) => (sectionRefs.current[0] = el)}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "360px 1fr",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#f3f7fb",
                  padding: isMobile ? "18px" : "26px",
                }}
              >
                <img
                  src="https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=900&q=80"
                  alt="Elite Care Physiotherapy"
                  style={{
                    width: "100%",
                    height: isMobile ? "300px" : "100%",
                    minHeight: isMobile ? "300px" : "420px",
                    objectFit: "cover",
                    borderRadius: "18px",
                    display: "block",
                  }}
                />
              </div>

              <div style={{ padding: isMobile ? "26px 18px" : "42px 42px" }}>
                <h2
                  className="section-title"
                  style={{ fontSize: isMobile ? "2rem" : "3rem" }}
                >
                  Who We Are
                </h2>

                <p className="section-text">
                  Elite Care Physiotherapy & Rehabilitation is a dedicated
                  center for advanced musculoskeletal, spine, and post-surgical
                  rehabilitation located in Kukatpally, Hyderabad.
                </p>

                <p className="section-text">
                  We are not just a physiotherapy clinic. We are a structured
                  rehabilitation centre focused on restoring mobility,
                  rebuilding strength, and improving quality of life through
                  evidence-based care.
                </p>

                <div
                  style={{
                    marginTop: "20px",
                    background: "#f8fbfd",
                    border: "1px solid #e7edf5",
                    borderRadius: "16px",
                    padding: "18px 20px",
                  }}
                >
                  <div
                    style={{
                      color: "#1f2e44",
                      fontWeight: 800,
                      fontSize: isMobile ? "17px" : "21px",
                      lineHeight: 1.6,
                    }}
                  >
                    Accurate Diagnosis. Structured Recovery. Long-Term Results.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MISSION + VISION */}
        <section style={{ padding: isMobile ? "0 16px 42px" : "0 24px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <div
              className={revealClass(1)}
              data-index={1}
              ref={(el) => (sectionRefs.current[1] = el)}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "22px",
              }}
            >
              <div className="page-card" style={{ padding: "30px 24px" }}>
                <div
                  className="icon-box"
                  style={{ background: "#e9fbf0", color: "#2ed67a" }}
                >
                  <FaBullseye />
                </div>

                <h2
                  className="section-title"
                  style={{ fontSize: isMobile ? "1.8rem" : "2.3rem" }}
                >
                  Our Mission
                </h2>

                <p className="section-text">
                  To provide ethical, scientific, and personalized
                  physiotherapy care that empowers patients to return to
                  pain-free, confident living.
                </p>

                <div style={{ display: "grid", gap: "10px", marginTop: "14px" }}>
                  {missionPoints.map((item, i) => (
                    <div key={i} className="check-item">
                      <FaCheckCircle
                        style={{ color: "#2ed67a", marginTop: "4px" }}
                      />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="page-card" style={{ padding: "30px 24px" }}>
                <div
                  className="icon-box"
                  style={{ background: "#edf6ff", color: "#2292ff" }}
                >
                  <FaRegEye />
                </div>

                <h2
                  className="section-title"
                  style={{ fontSize: isMobile ? "1.8rem" : "2.3rem" }}
                >
                  Our Vision
                </h2>

                <p className="section-text" style={{ margin: 0 }}>
                  To become one of the most trusted and result-oriented
                  physiotherapy and rehabilitation centres in Kukatpally and
                  Hyderabad, known for clinical precision and patient-centered
                  care.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CLINICAL APPROACH */}
        <section style={{ padding: isMobile ? "0 16px 42px" : "0 24px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <div
              className={`${revealClass(2)} page-card`}
              data-index={2}
              ref={(el) => (sectionRefs.current[2] = el)}
              style={{ padding: isMobile ? "26px 18px" : "34px 30px" }}
            >
              <h2
                className="section-title"
                style={{ fontSize: isMobile ? "2rem" : "2.6rem" }}
              >
                Our Clinical Approach
              </h2>

              <p className="section-text">
                At Elite Care, we follow a structured rehabilitation model. We
                believe that structured rehabilitation produces measurable and
                sustainable results.
              </p>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(5, minmax(0, 1fr))",
                  gap: "16px",
                  marginTop: "20px",
                }}
              >
                {clinicalApproach.map((item, index) => (
                  <div key={index} className="mini-card">
                    <div className="number-badge">{index + 1}</div>

                    <div
                      style={{
                        color: "#1f2e44",
                        fontWeight: 800,
                        fontSize: "15px",
                        lineHeight: 1.5,
                        marginBottom: "8px",
                      }}
                    >
                      {item.title}
                    </div>

                    <div
                      style={{
                        color: "#617186",
                        fontSize: "14px",
                        lineHeight: 1.8,
                      }}
                    >
                      {item.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* OUR TEAM */}
        <section style={{ padding: isMobile ? "0 16px 42px" : "0 24px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <h2
              className="section-title"
              style={{ fontSize: isMobile ? "2.2rem" : "3rem", textAlign: "center", marginBottom: "40px" }}
            >
              Meet Our Team
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
              {team.map((member, idx) => (
                <div
                  key={idx}
                  className={`${revealClass(3)} page-card`}
                  data-index={3}
                  ref={idx === 0 ? (el) => (sectionRefs.current[3] = el) : null}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "320px 1fr",
                    gap: "28px",
                    padding: isMobile ? "26px 18px" : "34px 30px",
                  }}
                >
                  <div>
                    <img
                      src={member.image}
                      alt={member.name}
                      style={{
                        width: "100%",
                        height: isMobile ? "300px" : "420px",
                        objectFit: "cover",
                        borderRadius: "18px",
                        display: "block",
                        boxShadow: "0 14px 28px rgba(15,23,42,0.08)",
                      }}
                    />
                    <div
                      style={{
                        marginTop: "16px",
                        background: "#f8fafc",
                        border: "1px solid #e7edf5",
                        borderRadius: "16px",
                        padding: "16px 16px",
                      }}
                    >
                      <div
                        style={{
                          color: "#1f2e44",
                          fontWeight: 800,
                          fontSize: "1.05rem",
                        }}
                      >
                        {member.name}
                      </div>
                      <div
                        style={{
                          color: "#2292ff",
                          fontWeight: 700,
                          fontSize: "14px",
                          marginTop: "2px",
                        }}
                      >
                        {member.role}
                      </div>
                      <div
                        style={{
                          color: "#617186",
                          marginTop: "4px",
                          fontSize: "15px",
                        }}
                      >
                        {member.qualifications}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h2
                      className="section-title"
                      style={{ fontSize: isMobile ? "1.6rem" : "2.2rem" }}
                    >
                      {member.name}
                    </h2>

                    {member.description.map((desc, i) => (
                      <p key={i} className="section-text">
                        {desc}
                      </p>
                    ))}

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        color: "#2292ff",
                        fontWeight: 800,
                        fontSize: "1.05rem",
                        marginTop: "18px",
                        marginBottom: "14px",
                      }}
                    >
                      <FaUserMd />
                      {member.interestsTitle}
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      {member.interests.map((item, index) => (
                        <div key={index} className="mini-card">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <FaCheckCircle style={{ color: "#2ed67a", flexShrink: 0 }} />
                            <span
                              style={{
                                color: "#4d5d72",
                                fontSize: "15px",
                                lineHeight: 1.6,
                              }}
                            >
                              {item}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CORE VALUES + SERVICE AREAS */}
        <section style={{ padding: isMobile ? "0 16px 42px" : "0 24px 56px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <div
              className={revealClass(4)}
              data-index={4}
              ref={(el) => (sectionRefs.current[4] = el)}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "22px",
              }}
            >
              <div className="page-card" style={{ padding: "30px 24px" }}>
                <h2
                  className="section-title"
                  style={{ fontSize: isMobile ? "2rem" : "2.3rem" }}
                >
                  Our Core Values
                </h2>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: "14px",
                  }}
                >
                  {values.map((item, index) => (
                    <div key={index} className="mini-card">
                      <div className="value-icon">{item.icon}</div>
                      <div
                        style={{
                          color: "#1f2e44",
                          fontWeight: 800,
                          marginBottom: "8px",
                        }}
                      >
                        {item.title}
                      </div>
                      <div
                        style={{
                          color: "#617186",
                          fontSize: "14px",
                          lineHeight: 1.8,
                        }}
                      >
                        {item.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="page-card" style={{ padding: "30px 24px" }}>
                <h2
                  className="section-title"
                  style={{ fontSize: isMobile ? "2rem" : "2.3rem" }}
                >
                  Serving the Community
                </h2>

                <p className="section-text">
                  Located in Vivekananda Nagar Colony, Kukatpally, we proudly
                  serve patients from nearby residential and commercial areas
                  across Hyderabad.
                </p>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: "12px",
                    marginTop: "18px",
                  }}
                >
                  {serviceAreas.map((item, index) => (
                    <div key={index} className="mini-card">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <FaMapMarkerAlt style={{ color: "#2ed67a" }} />
                        <span style={{ color: "#4d5d72", fontSize: "15px" }}>
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: "18px",
                    padding: "16px 18px",
                    borderRadius: "16px",
                    background: "#f8fbfd",
                    border: "1px solid #e7edf5",
                    color: "#1f2e44",
                    lineHeight: 1.85,
                    fontWeight: 700,
                  }}
                >
                  Recovery is not accidental.
                  <br />
                  It is structured, guided, and achieved step by step.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE US */}
        <section
          id="why-choose-us"
          style={{ padding: isMobile ? "0 16px 42px" : "0 24px 56px" }}
        >
          <div style={{ maxWidth: "1240px", margin: "0 auto" }}>
            <div
              className={`${revealClass(5)} page-card`}
              data-index={5}
              ref={(el) => (sectionRefs.current[5] = el)}
              style={{ padding: isMobile ? "26px 18px" : "34px 30px" }}
            >
              <div
                style={{
                  display: "inline-block",
                  padding: "10px 16px",
                  borderRadius: "999px",
                  background: "#eef7ff",
                  color: "#2292ff",
                  fontWeight: 800,
                  fontSize: "12px",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  marginBottom: "14px",
                }}
              >
                Why Choose Us
              </div>

              <h2
                className="section-title"
                style={{ fontSize: isMobile ? "2rem" : "2.6rem" }}
              >
                Choose Precision-Driven Rehabilitation
              </h2>

              <p className="section-text">
                Choosing the right physiotherapy centre is critical for
                long-term recovery. At Elite Care Physiotherapy &
                Rehabilitation, we combine clinical precision, structured
                rehabilitation protocols, and personalized care to deliver
                measurable outcomes.
              </p>

              <p
                style={{
                  color: "#1f2e44",
                  fontWeight: 800,
                  fontSize: isMobile ? "18px" : "22px",
                  marginTop: "10px",
                }}
              >
                We don’t just treat pain. We restore function.
              </p>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "16px",
                  marginTop: "20px",
                }}
              >
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="mini-card">
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #2ea7ff, #37d067)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                        marginBottom: "12px",
                      }}
                    >
                      {item.icon}
                    </div>

                    <div
                      style={{
                        color: "#1f2e44",
                        fontWeight: 800,
                        fontSize: "16px",
                        lineHeight: 1.5,
                        marginBottom: "8px",
                      }}
                    >
                      {item.title}
                    </div>

                    <div
                      style={{
                        color: "#617186",
                        fontSize: "14px",
                        lineHeight: 1.8,
                      }}
                    >
                      {item.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          style={{
            background: "#eaf2f8",
            padding: isMobile ? "54px 16px" : "70px 24px",
          }}
        >
          <div
            style={{
              maxWidth: "1100px",
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            <h2
              style={{
                margin: 0,
                color: "#15243a",
                fontSize: isMobile ? "2rem" : "3rem",
                lineHeight: 1.2,
                fontWeight: 800,
                fontFamily: "Georgia, serif",
              }}
            >
              Begin Your Recovery Journey Today
            </h2>

            <p
              style={{
                margin: "18px 0 30px",
                color: "#66758a",
                fontSize: isMobile ? "15px" : "18px",
                lineHeight: 1.7,
                maxWidth: "900px",
                marginInline: "auto",
              }}
            >
              If you are experiencing pain, stiffness, or post-surgical
              limitations, early intervention makes a difference. Book your
              consultation and take the first step toward structured recovery.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="hero-btn"
                style={{
                  textDecoration: "none",
                  background: "#2ed67a",
                  color: "#ffffff",
                  padding: "16px 28px",
                  borderRadius: "999px",
                  fontWeight: 800,
                  boxShadow: "0 12px 24px rgba(46,214,122,0.24)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                Book Consultation <FaWhatsapp />
              </a>

              <a
                href="tel:+919182643439"
                className="hero-button"
                style={{
                  textDecoration: "none",
                  background: "#ffffff",
                  color: "#26354b",
                  padding: "16px 28px",
                  borderRadius: "999px",
                  fontWeight: 800,
                  border: "1px solid #dde6ef",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                Contact Us <FaPhoneAlt />
              </a>
            </div>
          </div>
        </section>

        <Footer />

        <a
          href={whatsappLink}
          target="_blank"
          rel="noreferrer"
          className="whatsapp-float"
          title="Chat on WhatsApp"
        >
          <FaWhatsapp />
        </a>
      </div>
    </>
  );
} 